import { ReactNode } from 'react';
import { MessageDescriptor } from 'react-intl';
import styled, { css } from 'styled-components';

import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { TimeUntilNext } from '@sorare/core/src/atoms/ticker/TimeUntilNext';
import { LabelM, Title4 } from '@sorare/core/src/atoms/typography';

import { Cta } from './Cta';

export const shadowMargin = css`
  /* Account for extra margin from banners used to give box-shadows space */
  --banner-shadow-top-margin: var(--unit);
  --banner-shadow-bottom-margin: var(--unit);
`;

const Wrapper = styled.div`
  ${shadowMargin}
  position: relative;
  display: flex;
  align-items: center;
  gap: var(--unit);
  background: var(--c-neutral-200);
  color: var(--c-neutral-1000);
  padding: var(--unit);
  margin: var(--banner-shadow-top-margin) 0 var(--banner-shadow-bottom-margin);
  border-radius: var(--double-unit);
  cursor: pointer;
  &:not(.disabled):hover {
    transform: scale(1.01);
    box-shadow: var(--shadow-300);
  }
  &:hover > ${Cta} {
    color: var(--c-neutral-100);
    background: linear-gradient(
        rgba(var(--c-rgb-neutral-1000), 0.1),
        rgba(var(--c-rgb-neutral-1000), 0.2)
      )
      var(--c-brand-600);
  }
  &.disabled {
    color: var(--c-neutral-700);
    cursor: auto;
    &:hover > ${Cta} {
      color: var(--c-neutral-700);
      background: var(--c-neutral-400);
    }
  }
`;

const Text = styled(Vertical).attrs({ gap: 0.5 })`
  flex: 1 1 0%;
`;

const Title = styled(Title4)`
  font: var(--t-bold) var(--t-16);
  line-height: 1;
`;

const Subtitle = styled(LabelM)`
  line-height: 1.2;
`;

const TimeUntilNextStyled = styled(TimeUntilNext)`
  display: block;
  font: var(--t-12);
`;

type Props = {
  icon: ReactNode;
  title: ReactNode;
  subtitle: ReactNode;
  cta: ReactNode;
  timeLeftMessage: MessageDescriptor;
  timeLeftMessageVariables?: Record<string, any>;
  isAvailable: boolean;
  nextAvailableDate: Date;
  onClick: () => void;
};

export const GenericBanner = ({
  icon,
  title,
  subtitle,
  cta,
  timeLeftMessage,
  timeLeftMessageVariables,
  isAvailable,
  nextAvailableDate,
  onClick,
}: Props) => {
  return (
    <Wrapper
      className={isAvailable ? '' : 'disabled'}
      onClick={() => isAvailable && onClick()}
    >
      {icon}
      <Text>
        <Title>{title}</Title>
        <Subtitle>{subtitle}</Subtitle>
      </Text>

      <Cta as="button" role="button" disabled={!isAvailable}>
        {isAvailable ? (
          cta
        ) : (
          <TimeUntilNextStyled
            timeLeftMessage={timeLeftMessage}
            next={nextAvailableDate}
            variables={timeLeftMessageVariables}
          />
        )}
      </Cta>
    </Wrapper>
  );
};
